import { NonIdealState } from "@blueprintjs/core";
import { FormikProps } from "formik";
import { FGCustomPanel, FGListen, FGTextAreaInput, FieldGroup, InlineButtonContainer } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  DogBaseSearch,
  DogOwnerApi,
  EDogType,
  EPlatform,
  ERequestState,
  IPedigreeRequestDto,
  PedigreeRequestApi,
  PedigreeRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
} from "../../../../api";
import {
  ButtonsBloc,
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useAuth, useDialog, useEventsContext, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { RequestTransientPersonSelection } from "../../request/item/RequestTransientPersonSelection";

export interface IPedigreeRequestDetailPageProps {
  pedigreeRequestId: number;
  editMode: boolean;
}

export const PedigreeRequestDetailPage: React.FunctionComponent<IPedigreeRequestDetailPageProps> = ({
  pedigreeRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const { openNewTab } = useWindow();
  const history = useHistory();
  const { user } = useAuth();
  const api = useApiService(PedigreeRequestApi);
  const dogApi = useApiService(DogApi);
  const dogOwnerApi = useApiService(DogOwnerApi);
  const personApi = useApiService(PersonApi);
  const { showDialogPromise } = useDialog();
  const [dogIdValue, setDogIdValue] = React.useState<number>();
  const [ownerId, setOwnerId] = React.useState<number>();
  const { currentLanguage } = useGlobalData();

  const { data, loading, saving, saveItem, refresh } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: (id = pedigreeRequestId) =>
          id > 0
            ? api.getPedigreeRequest(id)
            : new PedigreeRequestDto({
                request: RequestDto.fromJS({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                }),
                transientOwnerIds: [],
              }).toJSON(),
        saveApiFn: (d: PedigreeRequestDto) => api.save(d),
        onSavedRoute: (d: PedigreeRequestDto) => `${ERoutes.rish}/${d.pedigreeRequestId}/detail/edit`,
      }),
      [api, pedigreeRequestId]
    ),
    [pedigreeRequestId]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      const result = await dogApi.isBelgianDogOwner(dogIdValue, ownerId);
      let isBanned = false;
      if (pedigreeRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (!result.value && data?.request?.status === ERequestState.REQUESTED) {
        const belgianConfirm = await showDialogPromise({
          message: t(ETLCodes.IsNotBelgianOwner),
          title: t(ETLCodes.Warning),
        });
        if (belgianConfirm !== "yes") return;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [data, dogApi, dogIdValue, ownerId, pedigreeRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const onDogIdChange = React.useCallback(
    async (value: number, formik: FormikProps<IPedigreeRequestDto>) => {
      if (user.platform === EPlatform.PORTAL) {
        return;
      }
      if (!formik?.values?.transientOwnerIds || formik?.values?.transientOwnerIds?.length === 0) {
        const fromDb = await dogOwnerApi.getIds(
          DogBaseSearch.fromJS({
            dogId: value,
            languageId: currentLanguage.languageId,
            currentOwners: true,
            showTemporaryOwners: true,
          })
        );
        console.log("From DB: ", fromDb);

        (window as any)._MYFORMIK = formik;
        setDogIdValue(value);
        await formik.setFieldValue("transientOwnerIds", fromDb);
      }
    },
    [currentLanguage.languageId, dogOwnerApi, user.platform]
  );

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
          const res = await api.isDogAvailable(value, pedigreeRequestId);
          return res.value;
        })
        .test("dog-correct-type", t(ETLCodes.DogMustBeExpoOrWithoutPedigree), async function (value) {
          const type = await dogApi.getDogType(value);
          return type === EDogType.ExpoDog || type === EDogType.DogWithoutPedigree;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (pedigreeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (pedigreeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (pedigreeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (pedigreeRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
            return !res.value;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [api, data, dogApi, dogIdValue, pedigreeRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.rish)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen
          field="request.personId"
          onChanged={(value) => {
            setOwnerId(value);
          }}
        />
        <FGListen field="dogId" onChanged={onDogIdChange} />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.RequestData),
            rightElement: pedigreeRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            ),
          }}
        >
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              info={
                pedigreeRequestId > 0 && (
                  <SharepointButton
                    existsInShp={dogExistsInShp}
                    loading={dogShpLoading}
                    onClick={openOrCreateDogInShp}
                  />
                )
              }
            />
            {user.platform === EPlatform.PORTAL ? null : (
              <FGPeopleSearchInput
                name="request.personId"
                label={t(ETLCodes.Contact)}
                onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
                dogId={dogIdValue}
                info={<InformationTooltip tooltipMessage={ETLCodes.OwnerMustHaveAddressInBelgium} />}
                showWarning={true}
              />
            )}
          </div>
          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
            rows={5}
          />
        </FieldGroup>
        {user.platform === EPlatform.PORTAL ? null : (
          <FGCustomPanel>
            {({ formik }) =>
              formik?.values?.dogId ? (
                <>
                  <RequestTransientPersonSelection
                    linkedIds={formik?.values?.transientOwnerIds || null}
                    onLinkedIdsChanged={(ids) => formik?.setFieldValue("transientOwnerIds", ids)}
                    editMode={editMode && !data?.request?.isBlocked && statusEditable}
                    loading={loading}
                  />
                </>
              ) : (
                <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectADogToEditOwners)} />
              )
            }
          </FGCustomPanel>
        )}
        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.rish}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
